import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  createMarketplaceRefund,
  getMarketplaceAdminBookings,
  processMarketplaceRelease,
  releaseMarketplacePayout,
  resolveMarketplaceDispute,
} from "../../api/myboothshareBilling";
import RentalsShell from "../manager/rentals/RentalsShell";
import { useAuth } from "../../context/AuthContext";
import {
  formatMarketplaceDateTime,
  formatMarketplaceMoneyFromCents,
  getMarketplaceBookingLabel,
  getMarketplaceReleaseLabel,
  getWorkspaceFinanceStateLabel,
} from "../../utils/myboothshareBillingPresentation";

const PAYMENT_TABS = [
  { key: "overview", label: "Overview" },
  { key: "due", label: "Due Now" },
  { key: "deposits", label: "Deposits" },
  { key: "payouts", label: "Payouts" },
  { key: "receipts", label: "Receipts" },
  { key: "disputes", label: "Disputes / Refunds" },
  { key: "ledger", label: "Ledger" },
];

const RESOLUTION_OPTIONS = [
  { value: "full_payout_owner", label: "Full payout to owner" },
  { value: "partial_payout_partial_refund", label: "Partial payout and partial refund" },
  { value: "full_refund_renter", label: "Full refund to renter" },
  { value: "reject_dispute_resume_payout", label: "Reject dispute and resume payout" },
];

function emptyResolutionState() {
  return {
    resolution_type: "reject_dispute_resume_payout",
    payout_amount_cents: "",
    refund_amount_cents: "",
    reason_code: "admin_resolution",
    resolution_notes: "",
  };
}

function safeRows(rows) {
  return Array.isArray(rows) ? rows : [];
}

function cents(value) {
  const amount = Number(value || 0);
  return Number.isFinite(amount) ? amount : 0;
}

function getFinanceStatus(record = {}) {
  const raw =
    record.finance_state_code ||
    record.payment_status ||
    record.release_status ||
    record.transfer_status ||
    record.booking_status ||
    "";
  const normalized = String(raw || "").toLowerCase();
  if (record.dispute_open) return "Disputed";
  if (normalized === "deposit_due") return "Deposit Due";
  if (normalized === "deposit_held") return "Deposit Held";
  if (normalized === "payment_captured" || normalized === "paid") return "Payment Captured";
  if (normalized === "payout_pending" || normalized === "pending") return "Payout Pending";
  if (normalized === "payout_released" || normalized === "released") return "Payout Released";
  if (normalized === "payout_on_hold" || normalized === "paused") return "Payout On Hold";
  if (normalized === "refunded") return "Refunded";
  if (normalized === "failed") return "Failed Payment";
  return getWorkspaceFinanceStateLabel(raw);
}

function isPaymentDue(record = {}) {
  const status = String(
    record.finance_state_code || record.payment_status || record.booking_status || ""
  ).toLowerCase();
  return (
    cents(record.due_now_cents) > 0 ||
    ["deposit_due", "payment_pending", "payment_method_required", "requires_payment"].includes(status)
  );
}

function isDepositRecord(record = {}) {
  const status = String(record.finance_state_code || record.payment_status || "").toLowerCase();
  return (
    cents(record.deposit_amount_cents) > 0 ||
    ["deposit_due", "deposit_held", "deposit_released"].includes(status)
  );
}

function isPayoutRecord(record = {}) {
  return (
    cents(record.net_payout_cents) > 0 ||
    Boolean(record.payout_hold_until_utc) ||
    Boolean(record.released_at_utc) ||
    ["released", "pending", "paused", "failed"].includes(
      String(record.transfer_status || record.release_status || "").toLowerCase()
    )
  );
}

function hasDisputeOrRefund(record = {}) {
  return record.dispute_open || safeRows(record.disputes).length > 0 || safeRows(record.refunds).length > 0;
}

function StatCard({ label, value, detail }) {
  return (
    <article className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="mt-3 text-3xl font-semibold text-slate-950">{value}</div>
      {detail ? <div className="mt-2 text-sm leading-6 text-slate-600">{detail}</div> : null}
    </article>
  );
}

function PaymentSummaryGrid({ record }) {
  return (
    <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
      {[
        ["Payment Required", formatMarketplaceMoneyFromCents(record.due_now_cents || record.amount_cents, record.currency)],
        ["Deposit State", getFinanceStatus(record)],
        ["Payout Pending", formatMarketplaceMoneyFromCents(record.net_payout_cents, record.currency)],
        ["Refunded", formatMarketplaceMoneyFromCents(record.refund_total_cents, record.currency)],
        [
          "Release Timing",
          record.released_at_utc
            ? formatMarketplaceDateTime(record.released_at_utc)
            : record.payout_hold_until_utc
            ? formatMarketplaceDateTime(record.payout_hold_until_utc)
            : "Pending",
        ],
      ].map(([label, value]) => (
        <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {label}
          </div>
          <div className="mt-2 text-base font-semibold text-slate-950">{value}</div>
        </div>
      ))}
    </div>
  );
}

function RentalPaymentCard({
  record,
  tab,
  refundForms,
  setRefundForms,
  resolutionForms,
  setResolutionForms,
  onProcess,
  onForceRelease,
  onRefund,
  onResolveDispute,
  setError,
}) {
  const disputes = safeRows(record.disputes);
  const refunds = safeRows(record.refunds);
  const auditLogs = safeRows(record.audit_logs);
  const receipts = safeRows(record.receipts);

  return (
    <article className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
            Rental Agreement #{record.booking_id}
          </p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-950">
            Workspace #{record.workspace_id || "n/a"}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            {record.renter_email || "Guest renter"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
            {getMarketplaceBookingLabel(record.booking_status)}
          </span>
          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
            {getMarketplaceReleaseLabel(record)}
          </span>
          <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
            {getFinanceStatus(record)}
          </span>
        </div>
      </div>

      <PaymentSummaryGrid record={record} />

      {["overview", "payouts"].includes(tab) ? (
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => onProcess(record.booking_id).catch((err) => setError(err.message))}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
          >
            Process release check
          </button>
          <button
            type="button"
            onClick={() => onForceRelease(record.booking_id).catch((err) => setError(err.message))}
            className="rounded-full bg-[#2f3566] px-4 py-2 text-sm font-semibold text-white hover:bg-[#252a53]"
          >
            Release payout
          </button>
        </div>
      ) : null}

      {["overview", "deposits"].includes(tab) ? (
        <section className="mt-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            Deposit state
          </p>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Deposit Held
              </div>
              <div className="mt-2 font-semibold text-slate-950">
                {formatMarketplaceMoneyFromCents(record.deposit_amount_cents, record.currency)}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Release Conditions
              </div>
              <div className="mt-2 font-semibold text-slate-950">
                {record.dispute_open ? "Payout on hold" : "Review window"}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Current State
              </div>
              <div className="mt-2 font-semibold text-slate-950">{getFinanceStatus(record)}</div>
            </div>
          </div>
        </section>
      ) : null}

      {["overview", "disputes"].includes(tab) ? (
        <div className="mt-5 grid gap-4 xl:grid-cols-[0.95fr,1.05fr]">
          <section className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Refund controls
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <input
                className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                placeholder="Refund amount cents"
                value={refundForms[record.booking_id]?.refund_amount_cents || ""}
                onChange={(event) =>
                  setRefundForms((prev) => ({
                    ...prev,
                    [record.booking_id]: {
                      ...(prev[record.booking_id] || {}),
                      refund_amount_cents: event.target.value,
                    },
                  }))
                }
              />
              <input
                className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                placeholder="Reason code"
                value={refundForms[record.booking_id]?.reason_code || "admin_refund"}
                onChange={(event) =>
                  setRefundForms((prev) => ({
                    ...prev,
                    [record.booking_id]: {
                      ...(prev[record.booking_id] || {}),
                      reason_code: event.target.value,
                    },
                  }))
                }
              />
              <textarea
                className="min-h-[110px] rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 md:col-span-2"
                placeholder="Notes"
                value={refundForms[record.booking_id]?.notes || ""}
                onChange={(event) =>
                  setRefundForms((prev) => ({
                    ...prev,
                    [record.booking_id]: {
                      ...(prev[record.booking_id] || {}),
                      notes: event.target.value,
                    },
                  }))
                }
              />
            </div>
            <button
              type="button"
              onClick={() => onRefund(record.booking_id).catch((err) => setError(err.message))}
              className="mt-3 rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600"
            >
              Issue refund
            </button>
          </section>

          <section className="space-y-4">
            {disputes.map((dispute) => {
              const state = resolutionForms[dispute.id] || emptyResolutionState();
              return (
                <article
                  key={dispute.id}
                  className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">
                        Dispute #{dispute.id}
                      </p>
                      <h3 className="mt-2 text-lg font-semibold text-slate-950">
                        {getMarketplaceBookingLabel(dispute.dispute_type)}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600">{dispute.summary}</p>
                    </div>
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                      {getMarketplaceBookingLabel(dispute.status)}
                    </span>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <select
                      className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                      value={state.resolution_type}
                      onChange={(event) =>
                        setResolutionForms((prev) => ({
                          ...prev,
                          [dispute.id]: {
                            ...state,
                            resolution_type: event.target.value,
                          },
                        }))
                      }
                    >
                      {RESOLUTION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <input
                      className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                      placeholder="Payout cents"
                      value={state.payout_amount_cents}
                      onChange={(event) =>
                        setResolutionForms((prev) => ({
                          ...prev,
                          [dispute.id]: {
                            ...state,
                            payout_amount_cents: event.target.value,
                          },
                        }))
                      }
                    />
                    <input
                      className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                      placeholder="Refund cents"
                      value={state.refund_amount_cents}
                      onChange={(event) =>
                        setResolutionForms((prev) => ({
                          ...prev,
                          [dispute.id]: {
                            ...state,
                            refund_amount_cents: event.target.value,
                          },
                        }))
                      }
                    />
                    <input
                      className="h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                      placeholder="Reason code"
                      value={state.reason_code}
                      onChange={(event) =>
                        setResolutionForms((prev) => ({
                          ...prev,
                          [dispute.id]: {
                            ...state,
                            reason_code: event.target.value,
                          },
                        }))
                      }
                    />
                    <textarea
                      className="min-h-[110px] rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 md:col-span-2"
                      placeholder="Resolution notes"
                      value={state.resolution_notes}
                      onChange={(event) =>
                        setResolutionForms((prev) => ({
                          ...prev,
                          [dispute.id]: {
                            ...state,
                            resolution_notes: event.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onResolveDispute(dispute.id).catch((err) => setError(err.message))}
                    className="mt-3 rounded-full bg-[#2f3566] px-4 py-2 text-sm font-semibold text-white hover:bg-[#252a53]"
                  >
                    Resolve dispute
                  </button>
                </article>
              );
            })}

            {!disputes.length ? (
              <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
                No disputes are attached to this rental agreement.
              </div>
            ) : null}
          </section>
        </div>
      ) : null}

      {["overview", "receipts", "disputes"].includes(tab) ? (
        <section className="mt-5 grid gap-4 xl:grid-cols-2">
          <article className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Receipts
            </p>
            <div className="mt-4 space-y-3">
              {receipts.map((receipt) => (
                <div
                  key={receipt.id}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
                >
                  <div className="font-semibold text-slate-950">{receipt.title || "Receipt"}</div>
                  <div className="mt-1">
                    {formatMarketplaceMoneyFromCents(receipt.amount_cents, receipt.currency)} -{" "}
                    {formatMarketplaceDateTime(receipt.issued_at)}
                  </div>
                </div>
              ))}
              {!receipts.length ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-500">
                  Receipts will appear after payments, refunds, or statements are issued.
                </div>
              ) : null}
            </div>
          </article>

          <article className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Refund history
            </p>
            <div className="mt-4 space-y-3">
              {refunds.map((refund) => (
                <div
                  key={refund.id}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
                >
                  <div className="font-semibold text-slate-950">
                    {formatMarketplaceMoneyFromCents(refund.refund_amount_cents, refund.currency)}
                  </div>
                  <div className="mt-1">
                    {refund.reason_code} - {getMarketplaceBookingLabel(refund.refund_status)}
                  </div>
                </div>
              ))}
              {!refunds.length ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-500">
                  No refunds recorded yet.
                </div>
              ) : null}
            </div>
          </article>
        </section>
      ) : null}

      {["overview", "ledger"].includes(tab) ? (
        <section className="mt-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            Ledger
          </p>
          <div className="mt-4 space-y-3">
            {auditLogs.map((entry) => (
              <div
                key={entry.id}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
              >
                <div className="font-semibold text-slate-950">{entry.action}</div>
                <div className="mt-1">{formatMarketplaceDateTime(entry.occurred_at_utc)}</div>
              </div>
            ))}
            {!auditLogs.length ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-500">
                No ledger entries recorded yet.
              </div>
            ) : null}
          </div>
        </section>
      ) : null}
    </article>
  );
}

export default function AdminMarketplaceBillingPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = PAYMENT_TABS.some((tab) => tab.key === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "overview";
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [filters, setFilters] = useState({ q: "", booking_status: "" });
  const [refundForms, setRefundForms] = useState({});
  const [resolutionForms, setResolutionForms] = useState({});

  const loadRecords = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getMarketplaceAdminBookings(token, {
        q: filters.q || undefined,
        booking_status: filters.booking_status || undefined,
      });
      setRecords(Array.isArray(response) ? response : []);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Unable to load marketplace payment records."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords().catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const metrics = useMemo(() => {
    const dueNow = records.reduce(
      (sum, record) => sum + cents(record.due_now_cents || (isPaymentDue(record) ? record.amount_cents : 0)),
      0
    );
    const deposits = records.reduce((sum, record) => sum + cents(record.deposit_amount_cents), 0);
    const payoutPending = records.reduce((sum, record) => sum + cents(record.net_payout_cents), 0);
    const refunds = records.reduce((sum, record) => sum + cents(record.refund_total_cents), 0);
    const disputes = records.reduce((sum, record) => sum + safeRows(record.disputes).length, 0);
    return { dueNow, deposits, payoutPending, refunds, disputes };
  }, [records]);

  const filteredRecords = useMemo(() => {
    if (activeTab === "due") return records.filter(isPaymentDue);
    if (activeTab === "deposits") return records.filter(isDepositRecord);
    if (activeTab === "payouts") return records.filter(isPayoutRecord);
    if (activeTab === "receipts") return records.filter((record) => safeRows(record.receipts).length > 0);
    if (activeTab === "disputes") return records.filter(hasDisputeOrRefund);
    if (activeTab === "ledger") return records.filter((record) => safeRows(record.audit_logs).length > 0);
    return records;
  }, [activeTab, records]);

  const handleTabChange = (tabKey) => {
    const next = new URLSearchParams(searchParams);
    if (tabKey === "overview") {
      next.delete("tab");
    } else {
      next.set("tab", tabKey);
    }
    setSearchParams(next, { replace: true });
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    await loadRecords();
  };

  const handleProcess = async (bookingId) => {
    setActionMessage("");
    await processMarketplaceRelease(token, bookingId);
    setActionMessage(`Processed release checks for rental agreement #${bookingId}.`);
    await loadRecords();
  };

  const handleForceRelease = async (bookingId) => {
    setActionMessage("");
    await releaseMarketplacePayout(token, bookingId, true);
    setActionMessage(`Payout released for rental agreement #${bookingId}.`);
    await loadRecords();
  };

  const handleRefund = async (bookingId) => {
    const form = refundForms[bookingId] || { refund_amount_cents: "", reason_code: "admin_refund", notes: "" };
    await createMarketplaceRefund(token, bookingId, {
      refund_amount_cents: Number(form.refund_amount_cents || 0),
      reason_code: form.reason_code || "admin_refund",
      notes: form.notes || null,
    });
    setActionMessage(`Refund issued for rental agreement #${bookingId}.`);
    await loadRecords();
  };

  const handleResolveDispute = async (disputeId) => {
    const form = resolutionForms[disputeId] || emptyResolutionState();
    await resolveMarketplaceDispute(token, disputeId, {
      resolution_type: form.resolution_type,
      payout_amount_cents: form.payout_amount_cents === "" ? null : Number(form.payout_amount_cents),
      refund_amount_cents: form.refund_amount_cents === "" ? null : Number(form.refund_amount_cents),
      reason_code: form.reason_code || "admin_resolution",
      resolution_notes: form.resolution_notes || null,
    });
    setActionMessage(`Resolved dispute #${disputeId}.`);
    await loadRecords();
  };

  return (
    <RentalsShell
      eyebrow="Ellyanna Boothshare Payments"
      title="Payments"
      description="Review payment required states, deposits, payout status, receipts, disputes, refunds, and ledger activity for workspace rental agreements."
    >
      <section className="space-y-6">
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Payment Required"
            value={formatMarketplaceMoneyFromCents(metrics.dueNow, "cad")}
            detail="Amounts currently due or awaiting collection."
          />
          <StatCard
            label="Deposit Held"
            value={formatMarketplaceMoneyFromCents(metrics.deposits, "cad")}
            detail="Deposits currently tracked for rental agreements."
          />
          <StatCard
            label="Payout Pending"
            value={formatMarketplaceMoneyFromCents(metrics.payoutPending, "cad")}
            detail="Owner payout amount before release checks complete."
          />
          <StatCard
            label="Disputed / Refunded"
            value={`${metrics.disputes} / ${formatMarketplaceMoneyFromCents(metrics.refunds, "cad")}`}
            detail="Open disputes and refund totals requiring review."
          />
        </section>

        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {PAYMENT_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  activeTab === tab.key
                    ? "bg-[#2f3566] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form
          className="grid gap-4 rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-[1fr,220px,auto]"
          onSubmit={handleSearch}
        >
          <label className="space-y-2">
            <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Search rentals
            </span>
            <input
              className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
              value={filters.q}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, q: event.target.value }))
              }
              placeholder="Rental agreement, workspace, or renter email"
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Agreement status
            </span>
            <input
              className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
              value={filters.booking_status}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, booking_status: event.target.value }))
              }
              placeholder="confirmed, active, disputed"
            />
          </label>
          <div className="flex items-end">
            <button
              type="submit"
              className="h-11 rounded-full bg-[#2f3566] px-5 text-sm font-semibold text-white hover:bg-[#252a53]"
            >
              Apply filters
            </button>
          </div>
        </form>

        {actionMessage ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {actionMessage}
          </div>
        ) : null}
        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        {loading ? <p className="text-sm text-slate-600">Loading payment records...</p> : null}

        {!loading && !filteredRecords.length ? (
          <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-600">
            No payment records match this view.
          </div>
        ) : null}

        <section className="grid gap-5">
          {filteredRecords.map((record) => (
            <RentalPaymentCard
              key={record.booking_id}
              record={record}
              tab={activeTab}
              refundForms={refundForms}
              setRefundForms={setRefundForms}
              resolutionForms={resolutionForms}
              setResolutionForms={setResolutionForms}
              onProcess={handleProcess}
              onForceRelease={handleForceRelease}
              onRefund={handleRefund}
              onResolveDispute={handleResolveDispute}
              setError={setError}
            />
          ))}
        </section>
      </section>
    </RentalsShell>
  );
}
